import { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { kpiMetricStore } from "./kpi-metric-store"
import KpiMetricCard from "./kpi-metric-card"
import { overviewStore } from "../../overview-store"

const KpiMetricView = () => {
  const { kpiMap } = kpiMetricStore()
  const { selectedTab } = overviewStore()

  const scrollRef = useRef(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const currentItems = kpiMap?.[selectedTab.id] ?? []

  const sortedItems = [...currentItems].sort((a, b) => {
    const aIsZero = a.value === 0
    const bIsZero = b.value === 0

    if (aIsZero === bIsZero) return 0

    return aIsZero ? 1 : -1
  })

  const checkScroll = () => {
    const element = scrollRef.current

    if (!element) return

    const maxScrollLeft = element.scrollWidth - element.clientWidth

    setShowLeft(element.scrollLeft > 5)
    setShowRight(maxScrollLeft > 5 && element.scrollLeft < maxScrollLeft - 5)
  }

  useEffect(() => {
    const element = scrollRef.current

    if (!element) return

    checkScroll()

    element.addEventListener("scroll", checkScroll, { passive: true })
    window.addEventListener("resize", checkScroll)

    const observer = new ResizeObserver(checkScroll)
    observer.observe(element)

    return () => {
      element.removeEventListener("scroll", checkScroll)
      window.removeEventListener("resize", checkScroll)
      observer.disconnect()
    }
  }, [sortedItems.length, selectedTab.id])

  const scrollByCard = (direction) => {
    const element = scrollRef.current
    const firstCard = element?.firstElementChild

    if (!element || !firstCard) return

    const gap = 12
    const cardWidth = firstCard.getBoundingClientRect().width

    element.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: "smooth"
    })
  }

  return (
    <section className="group relative min-w-0 w-full">
      <div
        ref={scrollRef}
        className="
          flex w-full min-w-0 gap-3
          overflow-x-auto overflow-y-hidden
          snap-x snap-mandatory scroll-hidden
        "
      >
        {sortedItems.map((item) => {
          const isDisabled = item.value === 0

          return (
            <div
              key={item.key}
              className={`min-w-60 flex-1 shrink-0 snap-center transition-opacity ${
                isDisabled ? "pointer-events-none opacity-40 grayscale" : ""
              }`}
            >
              <KpiMetricCard item={item} period={selectedTab.id} disabled={isDisabled} />
            </div>
          )
        })}
      </div>

      {showLeft && (
        <button
          type="button"
          aria-label="Previous KPI cards"
          onClick={() => scrollByCard(-1)}
          className="
            absolute left-2 top-1/2 z-10
            hidden size-9 -translate-y-1/2
            items-center justify-center
            rounded-full border border-slate-200
            bg-white/95 text-slate-600 shadow-md
            transition-opacity duration-200
            hover:bg-white hover:text-toicos-navy
            md:flex md:opacity-0
            md:group-hover:opacity-100
            md:group-focus-within:opacity-100
          "
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {showRight && (
        <button
          type="button"
          aria-label="Next KPI cards"
          onClick={() => scrollByCard(1)}
          className="
            absolute right-2 top-1/2 z-10
            hidden size-9 -translate-y-1/2
            items-center justify-center
            rounded-full border border-slate-200
            bg-white/95 text-slate-600 shadow-md
            transition-opacity duration-200
            hover:bg-white hover:text-toicos-navy
            md:flex md:opacity-0
            md:group-hover:opacity-100
            md:group-focus-within:opacity-100
          "
        >
          <ChevronRight size={20} />
        </button>
      )}
    </section>
  )
}

export default KpiMetricView
